<template>
    <mds-label
        :on-dark="onDark"
        :size="size"
        :optional="optional"
        :optional-text="optionalText"
        :required="required"
        :required-text="requiredText"
        :text="label"
        :for="ariaInputId"
        :id="ariaLabelId"
        :hidden-label="hiddenLabel"
    >
        <slot name="mds-microcopy-above">
            <mds-microcopy v-if="microcopyAbove" :size="size" v-html="microcopyAbove" :on-dark="onDark" />
        </slot>
        <component
            ref="childComponent"
            v-bind="$attrs"
            :is="selectType"
            :data-mds-version="version"
            :data-set="data"
            :error="error"
            :display-top="displayTop"
            :on-dark="onDark"
            :size="size"
            :disabled="disabled"
            :initial-selection="selectedItems"
            :placeholder="placeholder"
            :id="comboboxId()"
            :aria-label="ariaLabelId"
            :aria-input-id="ariaInputId"
            :multiple-item-limit="itemLimit"
            :required="required"
            @input="handleInputEvent"
        ></component>
        <div v-if="error" :class="$style['mds-combo-box__field-error-wrapper']" aria-live="assertive">
            <mds-field-error
                v-for="(text, index) in errorText"
                :key="index"
                v-show="error"
                :size="size"
                :text="text"
                :on-dark="onDark"
            />
        </div>
        <slot name="mds-microcopy-below">
            <mds-microcopy v-if="microcopyBelow" :size="size" v-html="microcopyBelow" :on-dark="onDark" />
        </slot>
    </mds-label>
</template>

<script>
    import MdsMultiSelect from './multi-select.vue';
    import MdsSingleSelect from './single-select.vue';
    import MdsFieldError from '@mds/field-error';
    import MdsLabel from '@mds/label';
    import MdsMicrocopy from '@mds/microcopy';
    import packageInfo from '../package.json';
    const { name, version } = packageInfo;

    export default {
        name: 'MdsComboBox',
        model: {
            prop: 'selectedItems',
        },
        inheritAttrs: false,
        components: {
            MdsMultiSelect,
            MdsLabel,
            MdsFieldError,
            MdsMicrocopy,
            MdsSingleSelect,
        },
        data() {
            return {
                version: `${name}-${version}`,
            };
        },
        props: {
            dataSet: {
                type: Array,
                docs: {
                    validation: '**Required**',
                    description: 'Sets items for the combo box.',
                },
            },
            disabled: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, sets `disabled` for the combo box.',
                },
            },
            displayTop: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, positions the list of items above the combo box input field.',
                },
            },
            error: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, triggers the error state and shows the `errorText` for the combo box.',
                },
            },
            errorText: {
                type: Array,
                docs: {
                    validation: '—',
                    description: 'Sets the error text that appears when `error=true` for the combo box.',
                },
            },
            hiddenLabel: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'When `true`, accessibly hides the combo box label.',
                },
            },
            label: {
                type: String,
                required: true,
                docs: {
                    validation: '**Required**',
                    description: 'Sets the label for the combo box.',
                },
            },
            multiple: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, allows the user to select multiple items at once.',
                },
            },
            microcopyAbove: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the microcopy above the combo box.',
                    support: ['v-html'],
                },
            },
            microcopyBelow: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the microcopy below the combo box.',
                    support: ['v-html'],
                },
            },
            multipleItemLimit: {
                type: Number,
                default: 5,
                docs: {
                    validation: 'Must be a number',
                    description:
                        'Sets the number of items a multiselect combo box will display before combining them into a single item which surfaces the number of items selected.',
                },
            },
            onDark: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, changes component to on-dark styling.',
                },
            },
            optional: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, displays the `optionalText` on the combo box label.',
                },
            },
            optionalText: {
                type: String,
                default: '(Optional)',
                docs: {
                    validation: '—',
                    description: 'Sets the label text to describe an combo box as optional.',
                },
            },
            placeholder: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the placeholder text for the combo box.',
                },
            },
            required: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, displays the required indicator (*) on the combo box label.',
                },
            },
            requiredText: {
                type: String,
                default: 'This field is required',
                docs: {
                    validation: '—',
                    description: 'Sets the `aria-label` text to describe an combo box as required.',
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium', 'large', 'touch'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `small`, `medium`, `large`, `touch`.',
                    description: 'Sets the size of the combo box.',
                },
            },
            selectedItems: {
                type: Array,
                docs: {
                    validation: '—',
                    description:
                        'An array of strings that sets the selected item(s) for the combo box. The string value(s) should match the `value` attribute of the `dataSet` item(s) that are selected.',
                    support: ['v-model'],
                },
            },
        },
        computed: {
            selectType() {
                return this.multiple ? MdsMultiSelect : MdsSingleSelect;
            },
            isGrouped() {
                return this.dataSet.find(item => item.hasOwnProperty('children')) !== undefined;
            },
            data() {
                return this.isGrouped ? this.flattenData(this.dataSet) : this.dataSet;
            },
            itemLimit() {
                return this.multiple ? this.multipleItemLimit : null;
            },
        },
        methods: {
            comboboxId() {
                const randomId = Math.random() * Math.floor(1000000);
                return `mds-combo-box-${Math.round(randomId)}`;
            },
            handleInputEvent(e) {
                this.$emit('input', e);
            },
            flattenData(dataArray) {
                return dataArray
                    .map(group => {
                        let groupArray = [];
                        if (group.children) {
                            groupArray.push({ text: group.text, value: null });
                            groupArray = groupArray.concat(
                                group.children.map(el => {
                                    el['child'] = true;
                                    return el;
                                })
                            );
                        } else {
                            groupArray = groupArray.concat(group);
                        }
                        return groupArray;
                    })
                    .reduce((acc, val) => acc.concat(val), []);
            },
        },
        created() {
            this.ariaLabelId = `aria-${this.comboboxId()}`;
            this.ariaInputId = this.$attrs.id ? this.$attrs.id : `aria-input-${this.comboboxId()}`;
        },
    };
</script>

<style lang="scss" module>
    .mds-combo-box__field-error-wrapper {
        display: block;
    }
</style>
